import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { searchApi } from '../api';

const TYPES = [
  { id: 'vertraege', label: 'Verträge', icon: '📋' },
  { id: 'dokumente', label: 'Dokumente', icon: '📄' },
  { id: 'zahlungen', label: 'Zahlungen', icon: '💳' },
  { id: 'ausgaben', label: 'Ausgaben', icon: '💰' },
  { id: 'kategorien', label: 'Kategorien', icon: '🏷️' },
];

const TYPE_META = {
  vertrag: { label: 'Vertrag', icon: '📋', tone: '#818cf8' },
  dokument: { label: 'Dokument', icon: '📄', tone: '#38bdf8' },
  zahlung: { label: 'Zahlung', icon: '💳', tone: '#34d399' },
  ausgabe: { label: 'Ausgabe', icon: '💰', tone: '#fbbf24' },
  kategorie: { label: 'Kategorie', icon: '🏷️', tone: '#f472b6' },
};

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [types, setTypes] = useState(new Set((searchParams.get('types') || '').split(',').filter(Boolean)));
  const [sort, setSort] = useState(searchParams.get('sort') || 'relevanz');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [dateFrom, setDateFrom] = useState(searchParams.get('date_from') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('date_to') || '');
  const [data, setData] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const activeParams = useMemo(() => ({
    types: Array.from(types).join(','),
    sort,
    status,
    date_from: dateFrom,
    date_to: dateTo,
    limit: 80,
  }), [types, sort, status, dateFrom, dateTo]);

  useEffect(() => {
    searchApi.history().then(res => setHistory(res.data.entries || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = query.trim();
      searchApi.suggestions(trimmed).then(res => setSuggestions(res.data)).catch(() => {});
      if (trimmed.length < 2) {
        setData(null);
        setError('');
        return;
      }

      setLoading(true);
      setError('');
      searchApi.global(trimmed, activeParams)
        .then(res => {
          setData(res.data);
          setSearchParams(Object.fromEntries(Object.entries({ q: trimmed, ...activeParams }).filter(([, value]) => value)));
        })
        .catch(() => setError('Suche konnte nicht geladen werden.'))
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(timer);
  }, [query, activeParams, setSearchParams]);

  function toggleType(type) {
    const next = new Set(types);
    if (next.has(type)) next.delete(type);
    else next.add(type);
    setTypes(next);
  }

  function openResult(result) {
    if (result?.url) navigate(result.url);
  }

  const groups = data?.results || {};
  const flat = data?.flatResults || [];
  const emptyMode = query.trim().length < 2;

  return (
    <div style={page}>
      <section style={header}>
        <div>
          <div style={eyebrow}>Enterprise Suche</div>
          <h1 style={title}>Suche</h1>
        </div>
        <input
          autoFocus
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Verträge, Dokumente, OCR, KI, Zahlungen, Ausgaben suchen"
          style={searchInput}
        />
      </section>

      <section style={shell}>
        <aside style={sidebar}>
          <FilterBlock title="Bereiche">
            {TYPES.map(type => (
              <label key={type.id} style={checkRow}>
                <input type="checkbox" checked={types.has(type.id)} onChange={() => toggleType(type.id)} />
                <span>{type.icon}</span>
                <span>{type.label}</span>
              </label>
            ))}
          </FilterBlock>
          <FilterBlock title="Sortierung">
            <select value={sort} onChange={e => setSort(e.target.value)} style={field}>
              <option value="relevanz">Relevanz</option>
              <option value="alphabet">Alphabet</option>
              <option value="datum">Datum</option>
              <option value="kosten">Kosten</option>
            </select>
          </FilterBlock>
          <FilterBlock title="Status & Datum">
            <input value={status} onChange={e => setStatus(e.target.value)} placeholder="Status" style={field} />
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={field} />
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={field} />
          </FilterBlock>
          <FilterBlock title="Zuletzt gesucht">
            {history.length === 0 ? <Empty text="Noch keine Suchhistorie." /> : history.slice(0, 8).map(item => (
              <button key={`${item.q}-${item.searched_at}`} onClick={() => setQuery(item.q)} style={historyBtn}>{item.q}</button>
            ))}
          </FilterBlock>
          <FilterBlock title="Beliebte Begriffe">
            <div style={pills}>
              {(suggestions?.topTerms || []).slice(0, 10).map(item => (
                <button key={item.term} onClick={() => setQuery(item.term)} style={pill}>{item.term}</button>
              ))}
            </div>
          </FilterBlock>
        </aside>

        <main style={resultsPanel}>
          {emptyMode ? (
            <StartState suggestions={suggestions} onPick={setQuery} />
          ) : loading ? (
            <State text="Suche läuft..." />
          ) : error ? (
            <State text={error} danger />
          ) : flat.length === 0 ? (
            <State text="Keine Treffer gefunden." />
          ) : (
            <>
              <div style={resultHeader}>
                <span>{data.total} Treffer</span>
                <span>{data.meta?.engine}</span>
              </div>
              {Object.entries(groups).filter(([, items]) => items.length > 0).map(([group, items]) => (
                <section key={group} style={groupBlock}>
                  <h2 style={groupTitle}>{groupLabel(group)} <span style={count}>{items.length}</span></h2>
                  {items.map(result => <ResultCard key={`${result.type}-${result.id}-${result.rank}`} result={result} onOpen={() => openResult(result)} />)}
                </section>
              ))}
            </>
          )}
        </main>
      </section>
    </div>
  );
}

function StartState({ suggestions, onPick }) {
  const contracts = suggestions?.recentContracts || [];
  return (
    <div style={startGrid}>
      <section style={groupBlock}>
        <h2 style={groupTitle}>Zuletzt verwendete Verträge</h2>
        {contracts.length === 0 ? <Empty text="Noch keine Verträge vorhanden." /> : contracts.map(contract => (
          <button key={contract.id} onClick={() => onPick(contract.title)} style={recentCard}>
            <strong>{contract.title}</strong>
            <span>{contract.subtitle || contract.meta}</span>
          </button>
        ))}
      </section>
      <section style={groupBlock}>
        <h2 style={groupTitle}>Top-Begriffe</h2>
        <div style={pills}>
          {(suggestions?.topTerms || []).map(item => <button key={item.term} onClick={() => onPick(item.term)} style={pill}>{item.term}</button>)}
        </div>
      </section>
    </div>
  );
}

function ResultCard({ result, onOpen }) {
  const meta = TYPE_META[result.type] || { label: result.type, icon: '🔎', tone: '#94a3b8' };
  return (
    <button onClick={onOpen} style={resultCard}>
      <span style={{ ...resultIcon, color: meta.tone, borderColor: `${meta.tone}66` }}>{meta.icon}</span>
      <span style={{ minWidth: 0, display: 'grid', gap: 4 }}>
        <span style={resultTop}>
          <strong style={resultTitle}>{result.title}</strong>
          <span style={score}>#{result.rank} · {result.score}</span>
        </span>
        <span style={resultMeta}>{meta.label} · {result.matchedField} · {result.matchedText || result.subtitle}</span>
        <span style={snippet}>{result.snippet || result.meta}</span>
      </span>
    </button>
  );
}

function FilterBlock({ title, children }) {
  return <section style={filterBlock}><h3 style={filterTitle}>{title}</h3>{children}</section>;
}

function State({ text, danger }) {
  return <div style={{ ...state, color: danger ? '#f87171' : '#94a3b8' }}>{text}</div>;
}

function Empty({ text }) {
  return <div style={empty}>{text}</div>;
}

function groupLabel(group) {
  return {
    vertraege: 'Verträge',
    dokumente: 'Dokumente',
    zahlungen: 'Zahlungen',
    ausgaben: 'Ausgaben',
    kategorien: 'Kategorien',
  }[group] || group;
}

const page = { padding: 18, maxWidth: 1320, margin: '0 auto', display: 'grid', gap: 16 };
const header = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 16, alignItems: 'end' };
const eyebrow = { color: '#38bdf8', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0 };
const title = { color: '#f8fafc', fontSize: 32, margin: '4px 0 0' };
const searchInput = { width: '100%', background: '#020617', border: '1px solid #334155', color: '#f8fafc', borderRadius: 8, padding: '14px 15px', fontSize: 16, boxSizing: 'border-box' };
const shell = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 16, alignItems: 'start' };
const sidebar = { display: 'grid', gap: 12 };
const filterBlock = { background: '#111827', border: '1px solid #1f2937', borderRadius: 8, padding: 13, display: 'grid', gap: 9 };
const filterTitle = { margin: 0, color: '#f8fafc', fontSize: 13 };
const checkRow = { display: 'flex', alignItems: 'center', gap: 8, color: '#cbd5e1', fontSize: 13 };
const field = { width: '100%', boxSizing: 'border-box', background: '#0f172a', border: '1px solid #334155', color: '#f8fafc', borderRadius: 7, padding: '9px 10px' };
const resultsPanel = { minHeight: 520, background: '#111827', border: '1px solid #1f2937', borderRadius: 8, padding: 14 };
const resultHeader = { display: 'flex', justifyContent: 'space-between', color: '#94a3b8', fontSize: 13, marginBottom: 12 };
const groupBlock = { display: 'grid', gap: 9, marginBottom: 16 };
const groupTitle = { color: '#f8fafc', fontSize: 16, margin: 0 };
const count = { color: '#64748b', fontSize: 12 };
const resultCard = { width: '100%', display: 'grid', gridTemplateColumns: '38px minmax(0, 1fr)', gap: 10, background: '#0f172a', border: '1px solid #263244', borderRadius: 8, padding: 12, color: '#f8fafc', textAlign: 'left', cursor: 'pointer' };
const resultIcon = { width: 34, height: 34, border: '1px solid', borderRadius: 8, display: 'grid', placeItems: 'center' };
const resultTop = { display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' };
const resultTitle = { overflowWrap: 'anywhere' };
const resultMeta = { color: '#94a3b8', fontSize: 12, overflowWrap: 'anywhere' };
const snippet = { color: '#cbd5e1', fontSize: 13, overflowWrap: 'anywhere' };
const score = { color: '#38bdf8', fontSize: 12, fontWeight: 800 };
const state = { minHeight: 320, display: 'grid', placeItems: 'center', fontSize: 14 };
const empty = { color: '#64748b', fontSize: 13 };
const pills = { display: 'flex', gap: 7, flexWrap: 'wrap' };
const pill = { background: '#0f172a', color: '#cbd5e1', border: '1px solid #334155', borderRadius: 999, padding: '5px 9px', cursor: 'pointer', fontSize: 12 };
const historyBtn = { background: 'transparent', color: '#cbd5e1', border: 0, textAlign: 'left', padding: '5px 0', cursor: 'pointer' };
const startGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 };
const recentCard = { display: 'grid', gap: 4, background: '#0f172a', border: '1px solid #263244', color: '#e5e7eb', borderRadius: 8, padding: 11, textAlign: 'left', cursor: 'pointer' };
